import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RefreshCw, Database, Server, Users, HardDrive, Activity } from 'lucide-react';
import { apiGet } from '@/lib/api/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { AdminErrorState, AdminLoadingState } from './index';

interface HealthData {
  database: {
    status: string;
    latency: number;
  };
  server: {
    uptime: number;
    platform: string;
    nodeVersion: string;
    memoryUsage: {
      used: number;
      total: number;
      percentage: number;
    };
    cpuLoad: number[];
  };
  users: {
    total: number;
    activeToday: number;
    byRole: {
      demo: number;
      company_user: number;
      company_admin: number;
      super_admin: number;
    };
  };
  companies: {
    total: number;
  };
  content: {
    total: number;
    byType: {
      presentation: number;
      pdf: number;
      image: number;
      video: number;
      markdown: number;
    };
  };
  storage: {
    uploadsDir: string;
    estimatedSize: string;
  };
}

export function SystemHealthMonitor() {
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { isAuthenticated, error: authError, checkAuth } = useAdminAuth();

  const fetchHealthData = async () => {
    try {
      // Validate authentication before making request
      const authValid = await checkAuth();
      if (!authValid) {
        throw new Error('Authentication required. Please log in.');
      }

      // Use centralized API client
      const data = await apiGet<HealthData>('/api/admin/health');
      setHealthData(data);
    } catch (error) {
      console.error('Error fetching health data:', error);
      // Error is already handled by apiGet (401 clears token, shows message)
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchHealthData();
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <AdminLoadingState
        title="System Health"
        description="Loading system health metrics..."
        skeletonCount={6}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <AdminErrorState
        title="System Health"
        error={authError || 'Authentication required. Please log in.'}
        onRetry={fetchHealthData}
        icon={<Activity className="h-5 w-5 text-destructive" />}
      />
    );
  }

  if (!healthData) {
    return (
      <AdminErrorState
        title="System Health"
        error="Unable to load system health data. Please try again."
        onRetry={fetchHealthData}
        icon={<Activity className="h-5 w-5 text-destructive" />}
      />
    );
  }

  const dbStatusColor = healthData.database.status === 'connected' ? 'text-green-600' : 'text-red-600';
  const memoryPercentage = healthData.server.memoryUsage.percentage;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Health
            </CardTitle>
            <CardDescription>Real-time platform health metrics</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Database Status */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span className="text-sm font-medium">Database</span>
            </div>
            <span className={`text-sm font-semibold ${dbStatusColor}`}>
              {healthData.database.status}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Latency: {healthData.database.latency}ms
          </p>
        </div>

        {/* Server Status */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            <span className="text-sm font-medium">Server</span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Uptime</p>
              <p className="font-semibold">{formatUptime(healthData.server.uptime)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Platform</p>
              <p className="font-semibold">{healthData.server.platform}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Node.js</p>
              <p className="font-semibold">{healthData.server.nodeVersion}</p>
            </div>
            <div>
              <p className="text-muted-foreground">CPU Load</p>
              <p className="font-semibold">
                {healthData.server.cpuLoad[0].toFixed(2)} / {healthData.server.cpuLoad[1].toFixed(2)} / {healthData.server.cpuLoad[2].toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Memory Usage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              <span className="text-sm font-medium">Memory Usage</span>
            </div>
            <span className="text-sm font-semibold">{memoryPercentage.toFixed(1)}%</span>
          </div>
          <Progress value={memoryPercentage} className="h-2" />
          <p className="text-xs text-muted-foreground">
            {formatBytes(healthData.server.memoryUsage.used)} / {formatBytes(healthData.server.memoryUsage.total)}
          </p>
        </div>

        {/* User Statistics */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="text-sm font-medium">Users</span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Total Users</p>
              <p className="font-semibold">{healthData.users.total}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Active Today</p>
              <p className="font-semibold">{healthData.users.activeToday}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Demo:</span>
              <span className="font-medium">{healthData.users.byRole.demo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Company Users:</span>
              <span className="font-medium">{healthData.users.byRole.company_user}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Company Admins:</span>
              <span className="font-medium">{healthData.users.byRole.company_admin}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Super Admins:</span>
              <span className="font-medium">{healthData.users.byRole.super_admin}</span>
            </div>
          </div>
        </div>

        {/* Company Statistics */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              <span className="text-sm font-medium">Companies</span>
            </div>
            <span className="text-sm font-semibold">{healthData.companies.total}</span>
          </div>
        </div>

        {/* Content Statistics */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              <span className="text-sm font-medium">Content Items</span>
            </div>
            <span className="text-sm font-semibold">{healthData.content.total}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Presentations:</span>
              <span className="font-medium">{healthData.content.byType.presentation}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">PDFs:</span>
              <span className="font-medium">{healthData.content.byType.pdf}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Images:</span>
              <span className="font-medium">{healthData.content.byType.image}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Videos:</span>
              <span className="font-medium">{healthData.content.byType.video}</span>
            </div>
            <div className="flex justify-between col-span-2">
              <span className="text-muted-foreground">Markdown:</span>
              <span className="font-medium">{healthData.content.byType.markdown}</span>
            </div>
          </div>
        </div>

        {/* Storage Information */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <HardDrive className="h-4 w-4" />
            <span className="text-sm font-medium">Storage</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Uploads directory: {healthData.storage.uploadsDir}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
